import { createReducer, Draft, PayloadAction } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { SentinelKey } from 'src/utils/socket';
import { authActions } from '../auth/actions';
import { logActions } from '../log/actions';
import { sentinelActions } from './actions';
import { SentinelState } from './types';
import { isCacheObjectExpired } from './utils';
import { persistReducer } from 'redux-persist';
import { capacitorStorage } from '../persist';
import { annotationActions } from '../annotation/actions';

const initialState: SentinelState = {
  controllers: { data: {}, query: {} },
  logs: { data: {}, query: {} },
  users: { data: {}, query: {} },
  firmware: { data: {}, query: {} },
  organizations: { data: {}, query: {} },
  annotations: { data: {}, query: {} },
  bundles: { data: {}, query: {} },
};

const pruneExpired = <T extends SentinelState[SentinelKey]>(collection: Draft<T>) => {
  for (const [key, item] of Object.entries(collection.data)) {
    if (isCacheObjectExpired(item)) delete collection.data[key];
  }
  for (const [key, item] of Object.entries(collection.query)) {
    if (isCacheObjectExpired(item)) delete collection.query[key];
  }
};

const _sentinelReducer = createReducer(initialState, builder =>
  builder
    .addCase(authActions.logOut, state => {
      return cloneDeep(initialState);
    })
    .addCase(sentinelActions.upsertOne, (state, { payload }) => {
      state[payload.key].data[payload.value._id] = { ...payload.value, fetchedAt: Date.now() };
    })
    .addCase(sentinelActions.updateOnePartial, (state, { payload }) => {
      if (!state[payload.key].data[payload.id]) return;
      state[payload.key].data[payload.id] = {
        ...state[payload.key].data[payload.id],
        ...(payload.value as any),
        fetchedAt: Date.now(),
      }; // TODO: Evaluate why type cast is necessary here. It should not be.
    })
    .addCase(sentinelActions.removeOne, (state, { payload }) => {
      delete state[payload.key].data[payload.id];
      for (const [key, item] of Object.entries(state[payload.key].query)) {
        if (item.items.includes(payload.id)) {
          state[payload.key].query[key].items = item.items.filter(i => i !== payload.id);
          state[payload.key].query[key].fetchedAt = Date.now();
        }
      }
    })
    .addCase(sentinelActions.upsertMany, (state, { payload }) => {
      for (const value of payload.value) {
        state[payload.key].data[value._id] = { ...value, fetchedAt: Date.now() };
      }
      state[payload.key].query[payload.query] = {
        items: payload.value.map(i => i._id),
        responseMeta: payload.responseMeta,
        fetchedAt: Date.now(),
      };
    })
    .addCase(sentinelActions.appendOne, (state, { payload }) => {
      state[payload.key].data[payload.value._id] = { ...payload.value, fetchedAt: Date.now() };
      if (payload.query && state[payload.key].query[payload.query]) {
        if (state[payload.key].query[payload.query].items.includes(payload.value._id)) return; // If we're updating an existing item, don't add it to the list.
        state[payload.key].query[payload.query].items.push(payload.value._id);
        state[payload.key].query[payload.query].fetchedAt = Date.now();
      }
    })
    .addCase(logActions.liveJobLogInitializeSuccess, (state, { payload }) => {
      state['logs'].data[payload.info._id] = { ...payload.info, fetchedAt: Date.now() };
    })
    .addCase(annotationActions.annotationSyncCommandSuccess, (state, { payload }) => {
      state['annotations'].data[payload.data._id] = { ...payload.data, fetchedAt: Date.now() };
      if (payload.type === 'create') {
        // We do this so that newly created annotations do not disappear from the UI for a short time while the remote data is being fetched.
        // This query is defined in AnnotationsContext.tsx. If it changes there, it needs to be changed here.
        const key = JSON.stringify({ query: { parentId: payload.data.parentId } });
        const queryData = state['annotations'].query[key];
        if (queryData) {
          if (queryData.items.includes(payload.data._id)) return;
          queryData.items.push(payload.data._id);
          queryData.fetchedAt = Date.now();
        }
      }
    })
    .addMatcher(
      (action): action is PayloadAction<{ key: SentinelKey }> => {
        return (
          action.type === sentinelActions.upsertOne.type ||
          action.type === sentinelActions.updateOnePartial.type ||
          action.type === sentinelActions.removeOne.type ||
          action.type === sentinelActions.upsertMany.type ||
          action.type === sentinelActions.appendOne.type
        );
      },
      (state, { payload }) => {
        pruneExpired(state[payload.key]);
      },
    )
    .addDefaultCase(s => s),
);

export const sentinelReducer = persistReducer(
  { key: 'sentinel', storage: capacitorStorage, throttle: 10000 },
  _sentinelReducer,
);
